import { useLayoutEffect, useState, type CSSProperties } from "react";
import { Icon, type IconName } from "@omnabase/ui";
import { dismiss, holdToasts, useToasts, type Toast, type ToastKind } from "./toast";

/** each kind's icon, coloured by kind on the toast's dark ground (the first setup round, 2026-10-22: Omnalingo's toast) */
const KIND_ICON: Record<ToastKind, IconName> = { success: "checkCircle", error: "error", warning: "warning", info: "info" };

/** On a phone a toast sits at the bottom, above whatever bar is there: the tab bar, or on a ticket the reply bar or the composer.
 *  The bars are sticky and differ per screen, so the bottom-most one is measured. (Ported from the prototype's Toasts.tsx.) */
function useBottomInset(active: boolean) {
  const [inset, setInset] = useState(0);
  useLayoutEffect(() => {
    if (!active) return;
    const measure = () => {
      const bars = [...document.querySelectorAll<HTMLElement>(".ob-tabbar, .ob-replybar, .ob-composer")].filter((el) => el.offsetParent !== null && el.getBoundingClientRect().height > 0);
      const top = Math.min(window.innerHeight, ...bars.map((el) => el.getBoundingClientRect().top));
      setInset(Math.max(0, window.innerHeight - top));
    };
    measure();
    const ro = new ResizeObserver(measure); ro.observe(document.body);
    window.addEventListener("resize", measure);
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
  });
  return inset;
}

/** More than one toast at a time is a stack: the newest in front, the older ones peeking out above it. The pointer over it (a desktop)
 *  or a tap on it (a phone) fans them out; the fan folds again when the pointer leaves, or on the next tap. Each toast still expires on its own.
 *  The pointer on any toast, a single one too, pauses the clocks; leaving starts them again with the time each still had (2026-10-23). */
export function Toasts() {
  const toasts = useToasts();
  const inset = useBottomInset(toasts.length > 0);
  const [open, setOpen] = useState(false);
  const [pointed, setPointed] = useState(false);
  const stacked = toasts.length > 1;
  useLayoutEffect(() => { if (!stacked) setOpen(false); }, [stacked]);
  useLayoutEffect(() => { if (toasts.length === 0) setPointed(false); }, [toasts.length]);
  const hold = open || pointed;
  useLayoutEffect(() => { holdToasts(hold); return () => holdToasts(false); }, [hold]); // under the pointer or fanned out, the clocks wait
  return (
    <div className={`pt-toasts${stacked ? " pt-toasts--stack" : ""}${stacked && open ? " pt-toasts--open" : ""}`} aria-live="polite" style={{ "--pt-bottom-inset": `${inset}px` } as CSSProperties}
      onMouseEnter={() => { if (!window.matchMedia("(hover: hover)").matches) return; setPointed(true); if (stacked) setOpen(true); }} onMouseLeave={() => { setPointed(false); setOpen(false); }} onClick={() => { if (stacked && !window.matchMedia("(hover: hover)").matches) setOpen((o) => !o); }}>
      {toasts.map((t, i) => <ToastRow key={t.id} t={t} behind={toasts.length - 1 - i} hidden={stacked && !open && i < toasts.length - 1} />)}
    </div>
  );
}

/** one toast: the kind's 30 px icon, the bold summary with its optional second line, the action (Undo) and the close control */
function ToastRow({ t, behind, hidden }: { t: Toast; behind: number; hidden: boolean }) {
  return (
    <div className={`pt-toast pt-toast--${t.kind}`} data-kind={t.kind} role={t.kind === "error" ? "alert" : undefined} style={{ "--pt-i": behind } as CSSProperties} aria-hidden={hidden ? true : undefined}>
      <span className="pt-toast__icon"><Icon name={KIND_ICON[t.kind]} /></span>
      <span className="pt-toast__text"><strong className="pt-toast__summary">{t.text}</strong>{t.detail && <span className="pt-toast__detail">{t.detail}</span>}</span>
      {t.action && <button type="button" className="pt-toast__action" onClick={(e) => { e.stopPropagation(); t.action!.onClick(); dismiss(t.id); }}>{t.action.label}</button>}
      <button type="button" className="pt-toast__close" aria-label="Close" onClick={(e) => { e.stopPropagation(); dismiss(t.id); }}><Icon name="x" size="sm" /></button>
    </div>
  );
}
